import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  website: '',
  message: '',
};

async function submitContactForm(payload) {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    formData.append(key, value);
  });

  const response = await fetch('https://formspree.io/f/mkgknebd', {
    method: 'POST',
    body: formData,
    headers: {
      Accept: 'application/json',
    },
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      json?.errors?.map((error) => error?.message).join(' ') ||
      json?.message ||
      `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  if (json?.errors?.length) {
    const message = json.errors.map((error) => error?.message).join(' ') || 'Form submission failed.';
    throw new Error(message);
  }

  return json;
}

const ContactCard = forwardRef(function ContactCard({ className = '' }, ref) {
  const sectionRef = useRef(null);
  const firstFieldRef = useRef(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState('');

  useImperativeHandle(
    ref,
    () => ({
      focusForm: () => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.setTimeout(() => firstFieldRef.current?.focus({ preventScroll: true }), 220);
      },
    }),
    []
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    const trimmedPhone = formData.phone.trim();
    const trimmedWebsite = formData.website.trim();

    const phonePattern = /^[+\d\s().-]{7,}$/;
    if (!phonePattern.test(trimmedPhone)) {
      setStatus('error');
      setErrorMessage('Please enter a valid phone number so we can reach you.');
      return;
    }

    const normalisedWebsite = trimmedWebsite.startsWith('http') ? trimmedWebsite : `https://${trimmedWebsite}`;

    try {
      // eslint-disable-next-line no-new
      new URL(normalisedWebsite);
    } catch {
      setStatus('error');
      setErrorMessage('Please enter a valid website URL (e.g. https://yourdomain.com).');
      return;
    }

    try {
      await submitContactForm({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        website: normalisedWebsite,
        message: formData.message.trim(),
      });

      setStatus('success');
      setFormData(INITIAL_FORM);
      window.setTimeout(() => firstFieldRef.current?.focus({ preventScroll: true }), 200);
    } catch (error) {
      console.error('Contact form submission failed', error);
      setStatus('error');
      setErrorMessage(error.message || 'Something went wrong. Please try again or email contact@anorcinc.com.');
    }
  };

  return (
    <section
      ref={sectionRef}
      id="contact"
      className={`relative overflow-hidden bg-gradient-to-br from-indigo-50 via-sky-50 to-white py-16 sm:py-20 ${className}`}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-12">
          <div className="order-2 space-y-4 lg:order-1">
            <p className="text-sm uppercase tracking-[0.25em] text-slate-500">Let’s talk</p>
            <h2 className="text-3xl font-semibold md:text-4xl">Tell us where revenue is stuck.</h2>
            <p className="max-w-xl text-slate-600">
              Call{' '}
              <a href="tel:14374944736" className="font-semibold text-slate-900 hover:underline">
                1‑437‑494‑4736
              </a>{' '}
              or send the form. We’ll reply with what we’d fix first and what it will cost—usually within a day.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white/70 p-4 shadow-sm">
                <p className="text-sm font-semibold text-slate-500">Avg. 90-day impact</p>
                <p className="mt-2 text-xl font-semibold text-slate-900">+178% qualified leads</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white/70 p-4 shadow-sm">
                <p className="text-sm font-semibold text-slate-500">Response time</p>
                <p className="mt-2 text-xl font-semibold text-slate-900">&lt; 24 hours</p>
              </div>
            </div>
          </div>
          <div className="order-1 rounded-3xl border border-slate-200 bg-white p-5 shadow-lg sm:p-6 lg:order-2">
            <form
              onSubmit={handleSubmit}
              className="grid gap-3 sm:gap-4"
              noValidate
              action="https://formspree.io/f/mkgknebd"
              method="POST"
            >
              <input
                ref={firstFieldRef}
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-slate-200 px-4 py-3"
                placeholder="Name"
                autoComplete="name"
              />
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-slate-200 px-4 py-3"
                placeholder="Email"
                autoComplete="email"
              />
              <input
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                pattern="^[+\d\s().-]{7,}$"
                className="w-full rounded-xl border border-slate-200 px-4 py-3"
                placeholder="Phone"
                autoComplete="tel"
              />
              <input
                name="website"
                type="url"
                value={formData.website}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-slate-200 px-4 py-3"
                placeholder="Website"
                autoComplete="url"
              />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="min-h-[120px] w-full rounded-xl border border-slate-200 px-4 py-3"
                placeholder="What are you trying to achieve?"
              />
              {status === 'success' && (
                <p className="text-sm text-green-600">Thanks! We received your request and will reply soon.</p>
              )}
              {status === 'error' && <p className="text-sm text-red-600">{errorMessage}</p>}
              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-2 w-full rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-5 py-3 text-white shadow hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
              >
                {status === 'loading' ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
});

export default ContactCard;
